import { Breakpoints, DesignTokenSizeEnum, Layout, List, ListItem, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

const StyledPrivacyStatementTitle = styled(Text)`
  margin: 0 0 ${(props) => props.theme.size.dynamic0100} 0;
`;

const StyledTitleText = styled(Text)`
  margin: 0 0 ${(props) => props.theme.size.dynamic0040} 0;
`;

const StyledSubTitleText = styled(Text)`
  margin: ${(props) => props.theme.size.dynamic0150} 0 ${(props) => props.theme.size.dynamic0100} 0;
`;

function SubTitleText({ children }: { children?: ReactNode }): ReactElement {
  return (
    <StyledSubTitleText appearance={TokenTextAppearance.copy200} bold>
      {children}
    </StyledSubTitleText>
  );
}

function TitleText({ children }: { children?: ReactNode }): ReactElement {
  return (
    <StyledTitleText appearance={TokenTextAppearance.headline300} bold>
      {children}
    </StyledTitleText>
  );
}

function ContentText({ children }: { children?: ReactNode }): ReactElement {
  return <Text appearance={TokenTextAppearance.copy200}>{children}</Text>;
}

function TextGroup({ children }: { children?: ReactNode }): ReactElement {
  return <Layout rowGap={DesignTokenSizeEnum.dynamic0100}>{children}</Layout>;
}

export function PrivacyStatementModalContentVwView(): ReactElement {
  return (
    <Layout rowGap={DesignTokenSizeEnum.dynamic0100} allowOverflowingContent>
      <Layout allowOverflowingContent>
        <StyledPrivacyStatementTitle appearance={TokenTextAppearance.headline400} bold>
          Privacy statement
        </StyledPrivacyStatementTitle>
        <Layout
          appearance={{
            [Breakpoints.default]: [{ name: 'a', columns: 24 }],
            [Breakpoints.b560]: [
              { name: '.', columns: 1 },
              { name: 'a', columns: 23 },
            ],
          }}
          allowOverflowingContent>
          <TextGroup>
            <ContentText>
              Volkswagen of South Africa (Pty) Limited (“VWSA/ we”/”us”) are committed to protecting and respecting any personal information
              you share with us. This statement applies to Personal Information provided to VWSA, its agents, partners, holding company and
              affiliates, including but not limited to its Dealer Partners and Volkswagen Financial Services of South Africa.{' '}
            </ContentText>
            <ContentText>
              In terms of the Protection of Personal Information Act (Section 18) we are required to provide you with this notification.
              This statement describes what types of information we collect from you or which we may collect from third parties, how it is
              used by us, how we share data with others, how you can manage the information we hold and how you can contact us. The
              information below describes processing of your personal information which we obtain through a variety of different
              interactions, including when you visit our websites, social media pages, Dealer Partners or contact our customer services
              centre.
            </ContentText>
            <ContentText>
              For the purposes of data protection laws, we are a Responsible Party, responsible for your personal information, which means
              we decide why and how it is processed. It also means we are responsible for that processing, which extends to those of our
              service providers who process your information based on our instructions. We work with other, more independent, organisations
              in connection with some of the processing activities described in this statement, such as social media platforms, authorised
              Dealer Partners and our group companies. Where that information is collected and sent to other organisations for processing
              that is in both our and their interests, we will be making decisions together in relation to that particular processing and
              will be jointly responsible with the organisations involved. We and the other organisations involved in making these decisions
              will be jointly responsible to you in terms of the Protection of Personal Information Act for this processing. In other
              circumstances, the organisation receiving your information will be separately responsible to you and use your personal
              information in the ways described in its privacy statement (and not ours).
            </ContentText>
            <ContentText>
              You will always have the option not to receive marketing communications from us. We will never send you unsolicited spam email
              or communications, or share your data with anyone else who might. We do not sell your information to third parties, but we do
              work closely with our authorised Dealer Partners, Volkswagen Financial Services South Africa and selected partners who help us
              to provide you with the information, products and services that you request from us. For example, the AA who provide roadside
              assistance on our behalf.
            </ContentText>
            <ContentText>
              The contents of this statement may change from time to time so you may wish to check this page occasionally to ensure you are
              still happy to share your information with us.
            </ContentText>
          </TextGroup>
        </Layout>
      </Layout>
      <Layout>
        <TitleText>What information do we collect?</TitleText>
        <TextGroup>
          <ContentText>
            We collect information about you and your vehicle when you engage with our website, applications or authorised Dealer Partner
            network. We only collect information, which is necessary, relevant and adequate for the purpose you are providing it for.
          </ContentText>
          <ContentText>The information we collect includes some or all of the following:</ContentText>
          <Layout
            appearance={{
              [Breakpoints.default]: [{ name: 'a', columns: 24 }],
              [Breakpoints.b560]: [
                { name: '.', columns: 1 },
                { name: 'a', columns: 23 },
              ],
            }}>
            <List isOrdered>
              <ListItem>Name (including title);</ListItem>
              <ListItem>Address</ListItem>
              <ListItem>Phone number;</ListItem>
              <ListItem>Date of birth;</ListItem>
              <ListItem>Email address;</ListItem>
              <ListItem>
                Vehicle information (including registration number, VIN, service reminders, mileage and warranty, service and maintenance
                repair information);
              </ListItem>
              <ListItem>The date and time you used our services;</ListItem>
              <ListItem>The pages you visited on our website and how long you visited us for;</ListItem>
              <ListItem>Your IP address;</ListItem>
              <ListItem>Your GPS location (where you have permitted access to this);</ListItem>
              <ListItem>The Internet browser and devices you are using;</ListItem>
              <ListItem>Cookie, Pixels or Beacon information (for more information please see our Cookie Policy );</ListItem>
              <ListItem>The website address from which you accessed our website;</ListItem>
              <ListItem>Details of any transactions between you and us or any authorised Dealer Partner;</ListItem>
              <ListItem>
                Where you engage with us in a business context, we may collect your job title, company contact details (including email
                addresses), fleet size and company details (some of which we may obtain from an online or public business directories);
              </ListItem>
              <ListItem>Voice recordings of calls you make to our customer service centre;</ListItem>
              <ListItem>“Web chat” records;</ListItem>
              <ListItem>Any information within correspondence you send to us including in any competition entries you submit;</ListItem>
              <ListItem>
                Your social media name and identification number, any information you post on our social media pages, posts in which you
                include a hashtag or mention relating to us and information regarding your activities on our social media pages generally
                (for example, the time and date of your posts and your likes): Your direct marketing preferences;
              </ListItem>
              <ListItem>
                Details of services provided by our group companies authorised Dealer Partners that you have received or for which you have
                registered;
              </ListItem>
              <ListItem>
                Information we receive about you from data cleansing, enrichment and other data analytics provider’s information about your
                race, gender and income scale in an aggregated data format and not a personalised data format.
              </ListItem>
              <ListItem>
                Where you act on behalf of a business, any information we collect as part of business telemarketing activity; and views and
                opinions as part of surveys
              </ListItem>
              <ListItem>
                Where your vehicle is financed by Volkswagen Financial Services South Africa we may collect basic agreement information
                (agreement end date, service plan details, extended guarantee information, instalment amounts, outstanding balance, finance
                agreement length and if the agreement is in good standing.).
              </ListItem>
              <ListItem>
                Where you sign up to single login, connected vehicle and other services provided by Volkswagen Group (VWAG) we may receive
                information about your account, profile or vehicle, including usage information.
              </ListItem>
              <ListItem>
                Any personal information that we obtain from you directly is provided to us on a voluntary basis. However, if you do not
                give this information to us, you may be unable to receive certain services from us or our partners or communicate with us
                effectively.
              </ListItem>
            </List>
          </Layout>
        </TextGroup>
      </Layout>
      <Layout>
        <TitleText>How do we use this information?</TitleText>
        <TextGroup>
          <ContentText>
            VWSA will only process information that is necessary for the purpose for which it has been collected. You will always have the
            option not to receive marketing communications from us (and you can withdraw your consent or object at any time). We will never
            send you unsolicited spam email or communications, or share your personal information with anyone else who might.
          </ContentText>
          <ContentText>There are various ways in which we may use or process your personal information. We list these below:</ContentText>
        </TextGroup>
        <SubTitleText>Consent:</SubTitleText>
        <TextGroup>
          <ContentText>Where you have provided your consent, we may use and process your information:</ContentText>
          <Layout
            appearance={{
              [Breakpoints.default]: [{ name: 'a', columns: 24 }],
              [Breakpoints.b560]: [
                { name: '.', columns: 1 },
                { name: 'a', columns: 23 },
              ],
            }}>
            <List>
              <ListItem>
                To contact you from time to time about promotions, events, products, services or information which we think may be of
                interest to you;
              </ListItem>
              <ListItem>
                To use algorithms, mathematical and statistical methods to create information and offers customised for you based on your
                information, including by making predictions about your behaviour.
              </ListItem>
            </List>
          </Layout>
          <ContentText>
            We rely on our legitimate interests as the legal reason to use your information for these purposes if we collect your
            information from another source.
          </ContentText>
          <ContentText>
            You can withdraw your consent at any time by contacting us on the details here or, in relation to any marketing messages you
            receive, by using the unsubscribe option included in those messages.
          </ContentText>
          <ContentText>
            We also process personal information we obtain from optional cookies that we set on this website based on your consent. For more
            information, please review our Cookie Policy.
          </ContentText>
        </TextGroup>
        <SubTitleText>Contractual performance:</SubTitleText>
        <ContentText>
          We may use and process your personal information where this is necessary to perform a contract with you and to fulfil and complete
          your orders, purchases and other transactions entered into with us (or one of our authorised Dealer Partners or selected
          partners).
        </ContentText>
        <SubTitleText>Legitimate Interests:</SubTitleText>
        <ContentText>
          We may use and process your personal information as set out below where it is necessary for us to carry out activities for which
          it is in our legitimate interests as a business to do so.
        </ContentText>
        <SubTitleText>Processing necessary for us to support customers with sales and other enquiries</SubTitleText>
        <TextGroup>
          <ContentText>
            1. To respond to correspondence you send to us and fulfil the requests you make to us (for example: test drives, service
            requests, brochure requests or information about specific vehicles);
          </ContentText>
          <ContentText>
            2. To provide vehicle support and services (for example warranty services and necessary interval services);
          </ContentText>
          <ContentText>3. To provide service, maintenance and warranty information and reminders, unless you object;</ContentText>
        </TextGroup>
        <SubTitleText>Processing necessary for us to respond to understanding customers and drivers’ needs</SubTitleText>
        <TextGroup>
          <ContentText>
            4. To analyse, evaluate and improve our products and services so that your visit and use of our website, applications, customer
            service centre and Dealer Partner network are more useful and enjoyable (we will generally use data amalgamated from many
            sources so that it doesn’t identify you personally);
          </ContentText>
          <ContentText>
            5. To undertake market analysis and research (including contacting you with customer surveys) so that we can better understand
            you as a customer and provide tailored offers, products and services that we think you will be interested in. We will only send
            such offers to you if you have provided your consent for us to do so;
          </ContentText>
          <ContentText>6. For product development purposes (for example to improve vehicle quality, performance and safety);</ContentText>
          <ContentText>
            7. We use automated methods to analyse, combine and evaluate information that you have provided to us (including by using
            information we receive from third party data providers and by receiving and sharing your personal information within our group
            of companies like Volkswagen Financial Services), see details about our relevant group companies below:
          </ContentText>
          <Layout
            appearance={{
              [Breakpoints.default]: [{ name: 'a', columns: 24 }],
              [Breakpoints.b560]: [
                { name: '.', columns: 1 },
                { name: 'a', columns: 23 },
              ],
            }}>
            <List>
              <ListItem>Volkswagen Financial Services South Africa (Pty) Limited</ListItem>
              <ListItem>Volkswagen AG</ListItem>
            </List>
          </Layout>
          <ContentText>
            We collect and analyse this information in this way so that we can deliver the most appropriate customer experience to you by
            tailoring and making relevant all our services and communications, including by making predictions about your current or future
            needs or interests (for example, when you might be ready to change your vehicle and the models you may be interested in);
          </ContentText>
        </TextGroup>
        <SubTitleText>
          Processing necessary for us to promote our business, brands and products and measure the reach and effectiveness of our campaigns
        </SubTitleText>
        <TextGroup>
          <ContentText>
            8. To contact you from time to time with marketing information after you have purchased a product or service from us, made a
            purchasing enquiry or requested a test drive, brochure or other information of interest. We will only contact you with
            information about our own products and services (and in ways the law allows), which we hope you will like. You have the right to
            object to us sending you this information at any time;
          </ContentText>
          <ContentText>
            9. To contact you from time to time with marketing information (unless you object) if you have expressly indicated to us that
            you are acting on behalf of a corporate business or where we have obtained your corporate business contact details from our
            authorised Dealer Partner network or an online or public business directory. In relation to any such information we send by
            email or SMS, we will include an option allowing you to object to receiving future messages by unsubscribing;{' '}
          </ContentText>
          <ContentText>
            10. To tailor the marketing information we send based on information we hold about you (e.g. by sending offers and information
            based on a vehicle you own).
          </ContentText>
          <ContentText>
            11. To use mathematical and statistical methods to create information and offers customised for you based on your information,
            including by making predictions about your behaviour. This may include predicting your preferences, suitable product
            recommendations, and your likelihood of making another purchase or your loyalty to a vehicle brand or model.
          </ContentText>
          <ContentText>
            12. To contact you with targeted advertising delivered online through social media, search engines, third party websites,
            applications, and other platforms operated by other companies, unless you object. You may receive advertising based on
            information about you that we have provided to the platform or allowed it to collect using cookies on our website or code in our
            applications (or a combination of the two). You may also receive advertising because, at our request, the platform has
            identified you as falling within a group whose attributes we have selected or a group that has similar attributes to the
            individuals whose details it has received from us (or a combination of the two). To find out more, please refer to the
            information provided in the help pages of the platforms on which you receive advertising from us. Please also see the section
            below for further information regarding our use of social media platforms specifically;
          </ContentText>
          <ContentText>
            13. To identify and record when you have received, opened or engaged with our website or electronic communications (please see
            our Cookie Policy for more information);
          </ContentText>
          <ContentText>
            14. To administer competitions and promotions that you enter with us from time to time and to distribute prizes;
          </ContentText>
          <ContentText>
            15. To send you information on our extended service and maintenance plans where you are benefiting from a current VWSA service
            or maintenance plan and it is about to expire. You have the right to object to us sending this information at any time;
          </ContentText>
          <ContentText>
            16. To identify the extent to which your interactions and communications with us or our content contributed to your engagement
            with us or our brand;
          </ContentText>
        </TextGroup>
        <SubTitleText>
          Processing necessary for us to communicate with you and to assess and respond to complaints, claims and regulators
        </SubTitleText>
        <TextGroup>
          <ContentText>
            17. To communicate with you (including through social media) including to respond to queries, complaints or claims and to manage
            legal and regulatory requests and requirements;
          </ContentText>
          <ContentText>18. To enforce or protect our legal rights or to establish, bring or defend legal claims;</ContentText>
        </TextGroup>
        <SubTitleText>
          Processing necessary for us to operate the administrative and technical aspects of our business efficiently and effectively
        </SubTitleText>
        <TextGroup>
          <ContentText>
            19. To verify the accuracy of information that we hold about you and create a better understanding of you as a customer. We may
            use information about you from third party data providers for this purpose, including verification and data cleansing service
            providers that provide the latest home address, contact number, e-mail address or vehicle ownership information for you;{' '}
          </ContentText>
          <ContentText>
            20. For network and information security purposes i.e. in order for us to take reasonable steps to protect your information
            against loss, damage, theft or unauthorised access;
          </ContentText>
          <ContentText>
            21. To comply with a request from you in connection with the exercise of your rights (for example where you have asked us not to
            contact you for marketing purposes, we will keep a record of this on our suppression lists in order to be able to comply with
            your request);
          </ContentText>
          <ContentText>22. To inform you of updates to our terms and conditions and policies;</ContentText>
          <ContentText>
            23. For cookies that are essential for our website to function properly. Please see our Cookie Policy for further information;
          </ContentText>
          <ContentText>
            24. To contact you when your local authorised Dealer Partner opens, closes, moves premises or is no longer an authorised Dealer
            Partner.
          </ContentText>
        </TextGroup>
        <SubTitleText>Legal Obligation:</SubTitleText>
        <ContentText>
          We may process your personal information to comply with our legal or regulatory requirements (for example to assist, or respond to
          a request for information from, an authorised authority, regulatory body or court, or to respond to a court order). These
          requirements include processing to remember your cookie consent preferences for optional cookies used by our website. Please see
          our Cookie Policy for further information.
        </ContentText>
        <SubTitleText>Vital Interest:</SubTitleText>
        <ContentText>
          Sometimes we will need to process your personal information to contact you if there is an urgent safety or product recall notice
          and we need to tell you about it.
        </ContentText>
      </Layout>
      <Layout>
        <TitleText>How do we share information?</TitleText>
        <ContentText>
          We do not sell your information to third parties, but we do work closely with our retailer network and with third party suppliers
          who fulfil business activities for us (like roadside assistance, marketing, events and market research etc.)
        </ContentText>
        <SubTitleText>Service providers:</SubTitleText>
        <ContentText>
          We do not sell your information to third parties. However, we may from time to time disclose your information to the following
          categories of companies or organisations to which we pass the responsibility to handle services on our behalf: roadside assistance
          service providers, customer contact centres, mobility and car hire providers, hosting providers, data cleansing and verification
          providers, direct marketing communications agencies and consultants, market research and market analytics service providers, our
          legal and other professional advisors.
        </ContentText>
        <SubTitleText>Dealer Partners:</SubTitleText>
        <ContentText>
          We will also share your information with our Dealer Partner network in situations where we need to pass your information to them
          in order to manage any request, provide a service or handle any complaint you may have made to us. For example, where you have
          requested a test drive, we will need to share your personal information with your selected Dealer Partner so that they can contact
          you to arrange for the test drive to take place.
        </ContentText>
        <SubTitleText>Sharing for legal reasons:</SubTitleText>
        <ContentText>
          We will also share your personal information if we have a duty to do so, e.g. to assist an authorised authority, regulatory body
          or court, or to enforce or protect our legal rights or to establish, bring or defend legal claims.
        </ContentText>
        <SubTitleText>Group companies:</SubTitleText>
        <ContentText>
          There are also circumstances where we share your information with our parent (or other companies within the VW Group) in order to
          understand you as a customer, fulfil orders, transactions, handle complaints or provide you with a service or information that you
          have requested. For example, we will share information with Volkswagen Financial Services South Africa (Pty) Limited where you
          enquire about their services through us or where you make a complaint to us and later make the same or similar complaint to them.
          Volkswagen Financial Services South Africa (Pty) Limited may use your information to provide you with information and carry out
          their obligations arising from contracts you enter into with them. For more information, please see their privacy statement.{' '}
        </ContentText>
        <SubTitleText>Social Media Platforms:</SubTitleText>
        <ContentText>
          We use a number of different social media platforms to communicate with you and to promote products and services. We process your
          personal information using these platforms in a variety of ways, as follows:
        </ContentText>
        <SubTitleText>Pages:</SubTitleText>
        <ContentText>
          We use your personal information when you post content or otherwise interact with us on our official pages on Facebook, Instagram,
          LinkedIn, Twitter, Snapchat, TikTok and other social media platforms. We also use the Page Insights service for Facebook,
          Instagram and LinkedIn to view statistical information and reports regarding your interactions with the pages we administer on
          those platforms and their content. Where those interactions are recorded and form part of the information we access through these
          Page Insights services, the relevant platform and we are joint controllers of the processing necessary to provide that service to
          us.
        </ContentText>
        <SubTitleText>Cookies:</SubTitleText>
        <TextGroup>
          <ContentText>
            We may make use of various social media platforms to further engage with you from time to time. We use cookies and similar
            technologies in our websites and applications to collect and send information to Facebook and LinkedIn about actions you take on
            our website and applications. In particular:
          </ContentText>
          <ContentText>
            Facebook uses this information to provide services to us and also for further processing for its own business purposes. Facebook
            and we are joint controllers of the processing involved in collecting and sending your personal information to Facebook using
            cookies and similar technologies as each of us has a business interest in Facebook receiving this information. You can find out
            more about these technologies by visiting our Cookie Policy. The services we receive from Facebook that use this information are
            delivered to us through Facebook’s Business Tools, which include Facebook Pixel, App Events SDK and Website Custom Audiences.
            These tools allow us to target advertising to you within Facebook’s social media platform by creating audiences based on your
            actions on our website and applications and allow Facebook to improve and optimise the targeting and delivery of our advertising
            campaigns for us.
          </ContentText>
        </TextGroup>
        <SubTitleText>Our relationship with Social Media Platforms</SubTitleText>
        <ContentText>
          The respective social media platform also processes, as our processor, personal information that we submit for the purposes of
          matching, online targeting, measurement, and reporting and analytics purposes. These services include the processing these
          platforms carry out when they display our advertisements to you in your news feed at our request after matching contact details
          for you that we have uploaded to them. These advertisements may include forms through which we collect contact information you
          give to us.
        </ContentText>
        <SubTitleText>Transfers outside the Republic of South Africa:</SubTitleText>
        <ContentText>
          Due to the international nature of our business, there may be some instances where your information is processed or stored outside
          of the Republic of South Africa and EU. In those instances, we will take reasonable measures to ensure that appropriate safeguards
          are in place for that transfer and storage as required by applicable law.
        </ContentText>
        <SubTitleText>Safeguards:</SubTitleText>
        <ContentText>
          We take reasonable steps to ensure that any third party partners who handle your information comply with data protection
          legislation and protect your information just as we do. We only disclose personal information that is necessary for them to
          provide the service that they are undertaking on our behalf. We will aim to anonymise your information or use aggregated none
          specific data sets wherever possible.
        </ContentText>
      </Layout>
      <Layout>
        <TitleText>How long do we keep information for?</TitleText>
        <TextGroup>
          <ContentText>
            We will not hold your personal information in an identifiable format for any longer than is necessary. If you are a customer or
            otherwise have a relationship with us we will hold personal information about you for a longer period than if we have obtained
            your details in connection with a prospective relationship.
          </ContentText>
          <ContentText>
            If we have a relationship with you (e.g. you are a customer, owner or the registered driver of a vehicle purchased or serviced
            from us or an authorised Dealer Partner), we hold your personal information for 16 years from the date our relationship ends. We
            hold your personal information after our relationship ends as required by other regulatory requirements. Our relationship may
            end for a number of reasons including where the vehicle warranty, or we have been made aware that you no longer own or drive
            that vehicle.
          </ContentText>
          <ContentText>
            Where we have obtained your personal information following a request for information, test drive, brochure, quotation or any
            other information on any of our products or services, we hold your personal information for an indefinite period from the date
            we collect that information, unless during that period you opt out of or unsubscribe from receiving marketing information.
          </ContentText>
          <ContentText>
            Where you have consented to receive marketing, regardless of whether or not we have a relationship with you, we will retain
            information we need to market to you until such time as you opt out or unsubscribe from receiving marketing information.
          </ContentText>
          <ContentText>
            We hold your personal information for the above periods to give us an opportunity to form a relationship with you.
          </ContentText>
          <ContentText>The only exceptions to the periods mentioned above are where:</ContentText>
          <Layout
            appearance={{
              [Breakpoints.default]: [{ name: 'a', columns: 24 }],
              [Breakpoints.b560]: [
                { name: '.', columns: 1 },
                { name: 'a', columns: 23 },
              ],
            }}>
            <List>
              <ListItem>
                The law requires us to hold your personal information for a longer period, or de-identify or delete it sooner;
              </ListItem>
              <ListItem>Where you have raised a legal claim, complaint or concern regarding a product or service offered by us.</ListItem>
            </List>
          </Layout>
        </TextGroup>
      </Layout>
      <Layout>
        <TitleText>How can you manage the information we hold about you?</TitleText>
        <TextGroup>
          <ContentText>
            You have the right as an individual to access your personal information we hold about you and make corrections if necessary and
            the right to object to the processing of your personal information. You also have the right to withdraw any consent you have
            previously given us and ask us to erase information we hold about you. You can also object to us using your personal information
            (where we rely on our business interests to process and use your personal information).{' '}
          </ContentText>
          <ContentText>
            You have a number of rights in relation to your personal information under data protection law. In relation to most rights, we
            will ask you for information to confirm your identity and, where applicable, to help us search for your personal information.
            Except in rare cases, we will respond to you within 30 days after we have received any request (including any identification
            documents requested).
          </ContentText>
          <ContentText>You have the right to:</ContentText>
          <Layout
            appearance={{
              [Breakpoints.default]: [{ name: 'a', columns: 24 }],
              [Breakpoints.b560]: [
                { name: '.', columns: 1 },
                { name: 'a', columns: 23 },
              ],
            }}>
            <List isOrdered>
              <ListItem> Ask for a copy of the information that we hold about you;</ListItem>
              <ListItem> Correct and update your information;</ListItem>
              <ListItem> Withdraw your consent (where we rely on it). Please see further How do we use this information;</ListItem>
              <ListItem>
                Object to our use of your information (where we rely on our legitimate interests to use your personal information) provided
                we do not have any continuing lawful reason to continue to use and process the information. When we do rely on our
                legitimate interests to use your personal information for direct marketing, we will always comply with your right to object;
              </ListItem>
              <ListItem>
                Erase your information (or restrict the use of it), provided we do not have any continuing lawful reason to continue to use
                and process that information;
              </ListItem>
              <ListItem>
                Transfer your information in a structured data file (in a commonly used and machine-readable format), where we rely on your
                consent to use and process your personal information or need to process it in connection with your contract.
              </ListItem>
            </List>
          </Layout>
          <ContentText>
            You can exercise the above rights and/or manage your information by contacting us using the details below:
          </ContentText>
          <ContentText>Contact details of the Responsible Party.</ContentText>
          <ContentText>Volkswagen: 0860 434 737</ContentText>
          <ContentText>Commercial Vehicles: 0860 103 349</ContentText>
          <ContentText>Facsimile: 041 394 5170</ContentText>
          <ContentText>All</ContentText>
          <ContentText>E-mail: support@vwconnect.co.za</ContentText>
          <ContentText>Postal address: PO Box 80, Uitenhage, South Africa, 6230</ContentText>
          <ContentText>Physical address: 103 Algoa Road, Uitenhage, South Africa, 6230</ContentText>
          <ContentText>
            If you have any specific data protection concerns or a complaint, you can address it to our Data Protection Team
            info.officer@vwsa.co.za
          </ContentText>
          <ContentText>
            If you are unhappy, you have the right to lodge a complaint with the Information Regulator. The contact details for the
            Information Regulator:
          </ContentText>
          <ContentText>Contact Details:</ContentText>
          <ContentText>Physical address: JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001</ContentText>
          <ContentText>Postal address: P.O Box 31533, Braamfontein, Johannesburg, 2017</ContentText>
          <ContentText>Complaints email: complaints.IR@justice.gov.za</ContentText>
          <ContentText>General enquiries email: inforeg@justice.gov.za.</ContentText>
        </TextGroup>
      </Layout>
    </Layout>
  );
}
